import { Component, ContentChild, ElementRef, EventEmitter, inject, Input, Output, output, TemplateRef, ViewEncapsulation } from '@angular/core';
import { ViewStateLayouts } from '@common/enums/viewstate-layouts';
import { Store } from '@ngrx/store';
import { LanguageSettings } from '@src/suite/common/services/language-settings';
import { selectCurrentLayout } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { DraggableEnpsComponent } from '@suite/common/components/base/draggable-enps-component';
import { DateTime } from '@suite/common/data-models/complex-types/date-time';
import { PSTFieldNames } from '@suite/common/data-models/fields/pst-field-names';
import { HeaderDirective } from '@suite/common/directives/header-directive';
import { ObjectTypes } from '@suite/common/enums/object-types';
import { GlobalTablesService } from '@suite/common/services/global-tables-service';
import { LayoutState } from '@suite/common/services/layout-state';
import moment from 'moment-timezone';

@Component({
    selector: 'st-cast-item',
    templateUrl: './cast-item.html',
    styleUrls: ['./cast-item.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-cast-item',
    },
})
export class CastItemComponent extends DraggableEnpsComponent {
    private store = inject(Store);

    get linkedTopic(): StorytellingTopicRecord {
        if (this.publication?.linkedRecords?.[0]?.Type === ObjectTypes.TOPIC) return this.publication?.linkedRecords[0];
        return null;
    }

    get linkedAssignment(): StorytellingAssignmentRecord {
        if (this.publication?.linkedRecords?.[0]?.Type === ObjectTypes.ASSIGNMENT) return this.publication?.linkedRecords[0];
        return null;
    }

    get story(): string {
        return this.languageSettings.PSTWebWireFullScreenStory;
    }

    get desk(): string {
        return this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTDesk]?.DefaultCaption;
    }

    get status(): string {
        return this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicStatus]?.DefaultCaption;
    }

    viewStateLayoutValue$ = this.store.select(selectCurrentLayout);

    openFullscreenHandler = output();

    constructor(
        public globalTablesService: GlobalTablesService,
        public languageSettings: LanguageSettings,
        public viewStateService: LayoutState,
        public el: ElementRef
    ) {
        super();
    }

    private _publication: StorytellingPublicationRecord = null;
    @Input()
    public get publication(): StorytellingPublicationRecord {
        return this._publication;
    }
    public set publication(v: StorytellingPublicationRecord) {
        this._publication = v;
        this._guid = v?.Guid;
    }

    @Input() public isReadOnly: boolean;
    @Input() public shouldShowCustomFields: boolean;
    @Input() public lastInGroup: boolean;
    @Input() public firstInGroup: boolean;
    pstFieldNames = PSTFieldNames;
    ObjectTypesEnum = ObjectTypes;
    @ContentChild(HeaderDirective, { read: TemplateRef }) itemTemplate;
    @Output() deletePublicationEmitter = new EventEmitter<StorytellingRecord>();

    public viewStateLayouts = ViewStateLayouts;

    public getDateTimeValue(id: string): string {
        const value = this.publication.linkedRecords?.[0]?.[id] || new DateTime();
        if (value) {
            if (value instanceof DateTime && value.get()) {
                if (value.get() && value.get().getUTCFullYear() !== 1800) {
                    return DateTime.formatDate(
                        DateTime.convertToTargetTZ(
                            value.get(),
                            this.globalTablesService.ClientTimeZone.id,
                            (this.globalTablesService.PersonalTimeZone ? this.globalTablesService.PersonalTimeZone : this.globalTablesService.ClientTimeZone).id
                        )
                    );
                }
                return '';
            }
            return DateTime.formatDate(new Date());
        }
        return '';
    }

    public getTimeControlValue(id: string): string {
        const value = this.publication.linkedRecords?.[0]?.[id] || new DateTime();
        let dateVal = new Date();
        if (!value) return '';
        if (value instanceof DateTime) {
            if (value.get() && value.get().getUTCFullYear() !== 1800) {
                dateVal = DateTime.convertToTargetTZ(
                    value.get(),
                    this.globalTablesService.ClientTimeZone.id,
                    (this.globalTablesService.PersonalTimeZone ? this.globalTablesService.PersonalTimeZone : this.globalTablesService.ClientTimeZone).id
                );
            } else {
                dateVal = null;
            }
        }

        if (dateVal) {
            const minutes = dateVal.getMinutes();
            const minutesString = minutes < 10 ? `0${minutes}` : minutes;
            const hours = dateVal.getHours();
            const hoursString = hours < 10 ? `0${hours}` : hours;
            if (dateVal.getUTCFullYear() !== 1800) {
                const time = `${hoursString.toString()}:${minutesString.toString()}`;
                return moment(time, 'LT').format('LT');
            }
        }
        return '';
    }

    getInnerProp(item: FieldDef) {
        const val = this.publication.linkedRecords[0]?.[item.ID]?.split('-')[0] || '';
        if (val) return val[item.ComplexPropInnerId];
        return '';
    }

    onFullscreenHandler(_data: StorytellingRecord) {
        this.openFullscreenHandler.emit();
    }

    public deletePublication(publication: StorytellingPublicationRecord) {
        this.deletePublicationEmitter.emit(publication);
    }
}
